// Helper functions for time tracking

import { getDayCoordinates, getDayKey } from "./roadmapHelpers";

/**
 * Default time estimate per task in minutes
 */
export const DEFAULT_TASK_TIME_MINUTES = 30;

/**
 * Extract time estimate from a day's tasks array
 * Looks for tasks starting with "Time:" and parses the minutes
 */
export function parseTimeFromTasks(tasks: string[]): number {
  const timeTask = tasks.find(task => 
    task.toLowerCase().startsWith('time:')
  );
  
  if (!timeTask) {
    return DEFAULT_TASK_TIME_MINUTES;
  }
  
  // Parse patterns like "Time: 45 minutes", "Time: 1 hour 30 minutes", etc.
  const match = timeTask.match(/(\d+)\s*(hour|hr|h|minute|min|m)/gi);
  
  if (!match) {
    return DEFAULT_TASK_TIME_MINUTES;
  }
  
  let totalMinutes = 0;
  // Handle hours and minutes
  match.forEach(part => {
    const value = parseInt(part);
    if (/hour|hr|h/i.test(part)) {
      totalMinutes += value * 60;
    } else {
      totalMinutes += value;
    }
  });
  
  return totalMinutes || DEFAULT_TASK_TIME_MINUTES;
}

/**
 * Calculate total estimated time for a day in minutes
 */
export function calculateDayTimeEstimate(taskCount: number): number {
  return taskCount * DEFAULT_TASK_TIME_MINUTES;
}

/**
 * Format minutes into human-readable time (e.g., "2h 30m", "45m")
 */
export function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Calculate total time invested based on completed days
 */
export function calculateTimeInvested(
  completedTasks: { [key: string]: boolean },
  roadmap: Array<{
    week: number;
    days: Array<{ day: number; tasks: string[] }>;
  }>
): {
  totalMinutes: number;
  totalHours: number;
  formattedTime: string;
} {
  let totalMinutes = 0;

  roadmap.forEach((week) => {
    week.days.forEach((day) => {
      const dayKey = getDayKey(week.week, day.day);
      if (completedTasks[dayKey]) {
        totalMinutes += parseTimeFromTasks(day.tasks);
      }
    });
  });

  const totalHours = Math.round((totalMinutes / 60) * 10) / 10; // Round to 1 decimal

  return {
    totalMinutes,
    totalHours,
    formattedTime: formatTime(totalMinutes),
  };
}

/**
 * Calculate total estimated time for entire roadmap
 */
export function calculateTotalRoadmapTime(
  roadmap: Array<{
    days: Array<{ tasks: string[] }>;
  }>
): {
  totalMinutes: number;
  totalHours: number;
  formattedTime: string;
} {
  let totalMinutes = 0;

  roadmap.forEach((week) => {
    week.days.forEach((day) => {
      totalMinutes += parseTimeFromTasks(day.tasks);
    });
  });

  const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

  return {
    totalMinutes,
    totalHours,
    formattedTime: formatTime(totalMinutes),
  };
}

/**
 * Get time estimate for a specific day
 */
export function getDayTimeEstimate(tasks: string[]): {
  minutes: number;
  formatted: string;
} {
  const minutes = parseTimeFromTasks(tasks);
  return {
    minutes,
    formatted: formatTime(minutes),
  };
}
